import { ButtonSize, ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import { MenuItem } from "@galaxy-io/dls/menu/Menu";
import { workflowProvider } from "@gtm/contracts/orchestration";
import { isDeliveryWorkflow } from "@gtm/contracts/workflows/providers";
import { PaperPlaneTiltIcon, PlayIcon, XIcon } from "@phosphor-icons/react";
import { useRouter, useUrlState } from "@/app/navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CompactSelect, SearchForm } from "@/components/workspace/people-styles";
import { SelectField } from "@/components/workspace/select-field";
import { useRetainedValue } from "@/components/workspace/use-retained-value";
import { apiClient, unwrap } from "@/lib/api-client";
import { GitHubWorkflowRunDialog } from "./github-workflow-run-dialog";
import { PersonEnrichmentDialog } from "./person-enrichment-dialog";
import { WorkflowLabel } from "./workflow-label";

const LAST_STATUS_OPTIONS = [
  { value: "", label: "Any last status" },
  { value: "never", label: "Never run" },
  { value: "running", label: "Running" },
  { value: "completed", label: "Completed" },
  { value: "failed", label: "Failed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "terminated", label: "Terminated" },
];

import { InputSize } from "@galaxy-io/dls/inputs/Input";
import type { WorkflowCatalogEntry } from "@gtm/contracts/workflows/catalog";
import { useQuery } from "@tanstack/react-query";
import { RecordTable } from "@/components/workspace/record-table";
import { Status } from "@/components/workspace/status";
import { BaseToolbar } from "@/components/workspace/toolbar";
import { date } from "@/lib/utils";

/**
 * The Library's list.
 *
 * `tasks` is widened deliberately. Each catalog entry infers its own tuple of
 * exact task names, so the server's plainer `{ key, name }` is not assignable to
 * it and every added task breaks this query's typing. The workflow key stays
 * narrow, because that one is checked against what the run endpoint accepts.
 */
type Catalog = Array<{
  key: WorkflowCatalogEntry["key"];
  name: string;
  description: string;
  schedule: string;
  tasks: Array<{ key: string; name: string }>;
  /** Added by the API on top of the shared catalog entry. */
  provider?: string;
  intent?: string;
  schedulable?: boolean;
}>;

export function WorkflowLauncher({
  repositories,
  lastRuns,
  initialCatalog,
}: {
  initialCatalog: WorkflowCatalogEntry[];
  repositories: Array<{
    id: string;
    name: string;
    enabled: boolean;
    provider: string;
  }>;
  lastRuns: Array<{
    workflow_key: string;
    requested_at: string;
    status: string;
  }>;
}) {
  // Typed to what the API returns, not to the literal catalog it starts from:
  // the catalog's inferred type is a union of exact string literals, so any new
  // workflow makes the server's wider type unassignable to it.
  const catalog = useQuery<Catalog>({
    queryKey: ["workflow-catalog"],
    initialData: initialCatalog.map((workflow) => ({
      ...workflow,
      tasks: [...workflow.tasks],
    })) as Catalog,
    queryFn: async ({ signal }) =>
      (await unwrap(apiClient().GET("/workflows", { signal }))) as Catalog,
  });
  const workflows = catalog.data;
  const { params, update } = useUrlState();
  const router = useRouter();
  const search = params.get("q") || "",
    lastStatus = params.get("status") || "";
  const term = search.trim().toLowerCase();
  const visible = workflows.filter((w) => {
    const last = lastRuns.find((r) => r.workflow_key === w.key);
    if (lastStatus && (lastStatus === "never" ? !!last : last?.status !== lastStatus)) return false;
    return (
      !term ||
      [w.name, w.description, w.schedule, w.key].some((value) => value.toLowerCase().includes(term))
    );
  });
  const filtering = !!search || !!lastStatus;
  const selectedWorkflow = workflows.find((workflow) => workflow.key === params.get("runWorkflow"));
  const retainedWorkflow = useRetainedValue(
    selectedWorkflow?.key !== "github.enrich_person" ? selectedWorkflow : null,
  );
  const matchingSources = repositories.filter((source) =>
    retainedWorkflow?.key.startsWith(`${source.provider}.`),
  );
  return (
    <>
      <BaseToolbar
        leadingActions={
          <>
            <SearchForm
              onSubmit={(event) => {
                event.preventDefault();
                update({
                  q: String(new FormData(event.currentTarget).get("q") || "") || null,
                });
              }}
            >
              <Input
                key={search}
                name="q"
                aria-label="Search workflows"
                placeholder="Search workflows…"
                defaultValue={search}
                type="search"
                size={InputSize.SMALL}
                label="Search workflows"
                hideLabel
              />
            </SearchForm>
            <CompactSelect $width={170}>
              <SelectField
                compact
                label="Last status"
                value={lastStatus}
                onChange={(status) => update({ status: status || null })}
                options={LAST_STATUS_OPTIONS}
                hideLabel
              />
            </CompactSelect>
            {filtering && (
              <Button
                label="Clear"
                ariaLabel="Clear workflow filters"
                icon={XIcon}
                variant={ButtonVariant.TERTIARY}
                onClick={() => update({ q: null, status: null })}
                size={ButtonSize.SMALL}
                tooltip="Clear workflow filters"
              />
            )}
          </>
        }
      />
      <RecordTable
        rowActions={(row) => row.actions}
        label="Workflows"
        columns={[
          { key: "name", label: "Workflow", width: 280, minWidth: 280, pin: "left" },
          { key: "tasks", label: "Tasks", width: "auto" },
          { key: "last", label: "Previously run", width: 200 },
          { key: "status", label: "Last status", width: 160 },
        ]}
        rows={visible.map((w) => {
          const last = lastRuns.find((r) => r.workflow_key === w.key);
          return {
            name: <WorkflowLabel source={workflowProvider(w.key) ?? ""} name={w.name} />,
            tasks: w.tasks.map((task) => task.name).join(" → "),
            last: last ? date(last.requested_at, true) : "Never run",
            status: last ? <Status value={last.status} /> : "—",
            // Deliveries run when people are sent, so their trigger lives
            // where the people are, never on a schedule.
            actions: isDeliveryWorkflow(w.key) ? (
              <MenuItem
                label={`Send people with ${w.name}`}
                icon={PaperPlaneTiltIcon}
                onSelect={() => router.push("/people")}
              />
            ) : (
              <MenuItem
                label={`Run ${w.name}`}
                icon={PlayIcon}
                onSelect={() => {
                  update({ runWorkflow: w.key, runSourceId: null });
                }}
              />
            ),
          };
        })}
        rowKeys={visible.map((w) => w.key)}
      />
      <PersonEnrichmentDialog
        open={selectedWorkflow?.key === "github.enrich_person"}
        onClose={() => update({ runWorkflow: null })}
        onStarted={(workflowId) =>
          router.push(`/workflows?view=history&runId=${encodeURIComponent(workflowId)}`)
        }
      />
      {retainedWorkflow && retainedWorkflow.key !== "github.enrich_person" && (
        <GitHubWorkflowRunDialog
          key={retainedWorkflow.key}
          open={!!selectedWorkflow && selectedWorkflow.key !== "github.enrich_person"}
          workflow={{ ...retainedWorkflow, key: retainedWorkflow.key }}
          repositories={matchingSources}
          sourceId={
            params.get("runSourceId") ?? (matchingSources.length === 1 ? matchingSources[0].id : "")
          }
          onSourceChange={(sourceId) => update({ runSourceId: sourceId }, true)}
          onClose={() => update({ runWorkflow: null, runSourceId: null })}
          restoreFocus={() =>
            document
              .querySelector<HTMLButtonElement>(
                `button[aria-label="${CSS.escape(`Actions for ${retainedWorkflow.name}`)}"]`,
              )
              ?.focus()
          }
        />
      )}
    </>
  );
}
